<template>
  <div>
    <div
      v-if="hasError"
      class="error-boundary"
    >
      <v-container>
        <v-row
          justify="center"
          align="center"
          class="error-boundary__content"
        >
          <v-col
            cols="12"
            sm="10"
            md="8"
            lg="6"
          >
            <v-card class="elevation-12">
              <v-card-title class="error-boundary__title">
                <v-icon
                  large
                  color="error"
                  class="mr-3"
                >
                  mdi-alert-circle
                </v-icon>
                Niečo sa pokazilo
              </v-card-title>

              <v-divider />

              <v-card-text class="pa-6">
                <div class="error-boundary__message">
                  <p class="text-h6 mb-4">
                    {{ errorMessage }}
                  </p>
                  
                  <p class="text-body-1 mb-4">
                    Ospravedlňujeme sa za nepríjemnosti. Skúste prosím:
                  </p>

                  <v-list dense>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon small>
                          mdi-refresh
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        Obnoviť stránku
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon small>
                          mdi-arrow-left
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        Vrátiť sa na predchádzajúcu stránku
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon small>
                          mdi-home
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        Prejsť na domovskú stránku
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>

                  <!-- Error details (development mode) -->
                  <v-expansion-panels
                    v-if="showDetails && errorDetails"
                    class="mt-4"
                  >
                    <v-expansion-panel>
                      <v-expansion-panel-header>
                        Technické detaily
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <pre class="error-boundary__details">{{ errorDetails }}</pre>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
              </v-card-text>

              <v-divider />

              <v-card-actions class="pa-4">
                <v-btn
                  color="primary"
                  @click="reload"
                >
                  <v-icon left>
                    mdi-refresh
                  </v-icon>
                  Obnoviť stránku
                </v-btn>

                <v-btn
                  text
                  @click="goBack"
                >
                  <v-icon left>
                    mdi-arrow-left
                  </v-icon>
                  Späť
                </v-btn>

                <v-spacer />

                <v-btn
                  text
                  color="primary"
                  @click="goHome"
                >
                  <v-icon left>
                    mdi-home
                  </v-icon>
                  Domov
                </v-btn>
              </v-card-actions>
            </v-card>

            <!-- Report issue button -->
            <div class="text-center mt-4">
              <v-btn
                v-if="onReport"
                text
                small
                color="grey"
                @click="reportIssue"
              >
                <v-icon
                  left
                  small
                >
                  mdi-bug
                </v-icon>
                Nahlásiť problém
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
    
    <slot v-else />
  </div>
</template>

<script>
export default {
  name: 'ErrorBoundary',
  
  props: {
    fallbackMessage: {
      type: String,
      default: 'Aplikácia narazila na neočakávanú chybu.',
    },
    
    showDetails: {
      type: Boolean,
      default: process.env.NODE_ENV === 'development',
    },
    
    onError: {
      type: Function,
      default: null,
    },
    
    onReport: {
      type: Function,
      default: null,
    },
    
    resetOnRouteChange: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  },

  computed: {
    errorMessage() {
      if (this.error?.message) {
        return this.error.message;
      }
      return this.fallbackMessage;
    },

    errorDetails() {
      if (!this.error) return null;

      return {
        message: this.error.message,
        stack: this.error.stack,
        info: this.errorInfo,
        timestamp: new Date().toISOString(),
      };
    },
  },

  watch: {
    '$route'() {
      if (this.resetOnRouteChange && this.hasError) {
        this.resetError();
      }
    },
  },

  errorCaptured(err, instance, info) {
    this.hasError = true;
    this.error = err;
    this.errorInfo = info;

    // Log to console in development
    // Removed for production build

    // Call custom error handler
    if (this.onError) {
      this.onError(err, instance, info);
    }

    // Prevent error from propagating
    return false;
  },

  methods: {
    reload() {
      window.location.reload();
    },

    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.goHome();
      }
    },

    goHome() {
      this.$router.push('/').catch(() => {
        window.location.href = '/';
      });
    },

    resetError() {
      this.hasError = false;
      this.error = null;
      this.errorInfo = null;
    },

    reportIssue() {
      if (this.onReport) {
        this.onReport({
          error: this.error,
          errorInfo: this.errorInfo,
          userAgent: navigator.userAgent,
          url: window.location.href,
        });
      }
    },
  },
};
</script>

<style scoped>
.error-boundary {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  padding: 24px;
}

.error-boundary__content {
  min-height: 50vh;
}

.error-boundary__title {
  font-size: 1.5rem;
  font-weight: 500;
  color: #d32f2f;
}

.error-boundary__message {
  line-height: 1.6;
}

.error-boundary__details {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  line-height: 1.4;
  color: #333;
  max-height: 300px;
}

@media (max-width: 600px) {
  .error-boundary {
    padding: 16px;
  }

  .error-boundary__title {
    font-size: 1.25rem;
  }
}
</style>
